import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { dictionary } from '../../../dictionary';

const FilteredSummaryDialog = ({ visible, onHide, data, allInvoices, currentGJ, onCreateGoogleSheet }) => {
  const [maximized, setMaximized] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const formatValue = (val, type) => {
    if (val === '' || val === null || val === undefined) return '-';
    if (val === 0 || val === '0') {
      if (type === 'EUR') return '0.00 €';
      if (type === 'PLN') return '0.00 PLN';
      return '0';
    }
    if (type === 'EUR' || type === 'PLN') {
      const num = Number(val);
      if (isNaN(num)) return val;
      const formatted = num.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return type === 'EUR' ? `${formatted} €` : `${formatted} PLN`;
    }
    return val;
  };

  const getGJFromDate = (dateInput) => {
    if (!dateInput) return null;
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return null;
    return date.getMonth() + 1 >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  };

  const targetGJ = useMemo(() => {
    const y = parseInt(currentGJ, 10);
    if (isNaN(y)) return new Date().getFullYear();
    return y < 100 ? 2000 + y : y;
  }, [currentGJ]);

  const reportMetrics = useMemo(() => {
    if (!data || !allInvoices) return [];
    const logIdsInView = new Set(data.flatMap((g) => g.entries?.map((e) => e._id) || []));
    const filteredInvoices = allInvoices.filter((inv) => inv.entries?.some((e) => logIdsInView.has(e._id)));

    let sumPaidInGJ = 0;
    let sumPayableInGJ = 0;
    let modulesInGJ = 0;

    filteredInvoices.forEach((inv) => {
      if (inv.invoiceStatus === 'withdrawn') return;
      const mainGJ = inv._computedGJ || getGJFromDate(inv.dateInvoice1);
      if (mainGJ === targetGJ) modulesInGJ++;

      // if (getGJFromDate(inv.dateInvoice1) === targetGJ) {
      //   sumPaidInGJ += Number(inv.netPaid1Invoice) || 0;
      //   sumPayableInGJ += Number(inv.netPayable) || 0;
      // }
      // if (getGJFromDate(inv.dateInvoice2) === targetGJ) {
      //   sumPaidInGJ += Number(inv.netPaid2Invoice) || 0;
      // }
      
      // ZMIANA do tego co wyżej jest zakomentowane: Sumę zapłaconą "Paid" zliczamy używając dat potwierdzenia płatności
      if (getGJFromDate(inv.payment1Confirmed) === targetGJ) {
        sumPaidInGJ += Number(inv.netPaid1Invoice) || 0;
      }
      if (getGJFromDate(inv.payment2Confirmed) === targetGJ) {
        sumPaidInGJ += Number(inv.netPaid2Invoice) || 0;
      }

      // Kwota "do zapłaty" wciąż musi patrzeć na datę rejestracji faktury (bo nie ma jeszcze płatności)
      if (getGJFromDate(inv.dateInvoice1) === targetGJ) {
        sumPayableInGJ += Number(inv.netPayable) || 0;
      }
      ///Koniec zmiany
    });

    return [
      {
        id: 1,
        name: dictionary.esp_filtered_metric_paid_in?.replace('{gj}', targetGJ),
        value: `${sumPaidInGJ.toFixed(2)} PLN`,
        desc: dictionary.esp_filtered_metric_desc_1?.replace('{gj}', targetGJ),
      },
      {
        id: 2,
        name: dictionary.esp_filtered_metric_payable_in?.replace('{gj}', targetGJ),
        value: `${sumPayableInGJ.toFixed(2)} PLN`,
        desc: dictionary.esp_filtered_metric_desc_2?.replace('{gj}', targetGJ),
      },
      {
        id: 3,
        name: dictionary.esp_filtered_metric_modules_in?.replace('{gj}', targetGJ),
        value: modulesInGJ,
        desc: dictionary.esp_filtered_metric_desc_3?.replace('{gj}', targetGJ),
      },
      {
        id: 4,
        name: dictionary.esp_filtered_metric_total_modules,
        value: filteredInvoices.length,
        desc: dictionary.esp_filtered_metric_desc_4,
      },
      {
        id: 5,
        name: dictionary.esp_filtered_metric_lifetime,
        value: `${filteredInvoices.reduce((acc, i) => acc + (Number(i.netPaid1Invoice) || 0) + (Number(i.netPaid2Invoice) || 0), 0).toFixed(2)} PLN`,
        desc: dictionary.esp_filtered_metric_desc_5,
      },
    ];
  }, [data, allInvoices, targetGJ]);

  const excelTableData = useMemo(() => {
    if (!data || !allInvoices) return [];
    return data.map((logGroup) => {
      const firstLogId = logGroup.entries?.[0]?._id;
      const inv = allInvoices.find((i) => i.entries?.some((e) => e._id === firstLogId)) || {};
      const stagesSet = new Set(inv.entries?.map((e) => e.stage).filter(Boolean) || []);
      const convRate = Number(inv.conv);
      const hasConv = !isNaN(convRate) && convRate > 0;

      const calcEur = (plnVal) => {
        const val = Number(plnVal);
        if (isNaN(val) || !hasConv) return 0;
        return val / convRate;
      };

      const formatDate = (d) => {
        if (!d) return '-';
        const date = new Date(d);
        if (isNaN(date.getTime())) return '-';
        return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
      };

      const netPaid1 = Number(inv.netPaid1Invoice) || 0;
      const netPaid2 = Number(inv.netPaid2Invoice) || 0;
      const grossPaid1 = Number(inv.grossPaid1Invoice) || 0;
      const grossPaid2 = Number(inv.grossPaid2Invoice) || 0;

      const totalNetPaid = netPaid1 + netPaid2;
      const totalGrossPaid = grossPaid1 + grossPaid2;

      return {
        ...logGroup,
        _gj: inv._computedGJ || logGroup._computedGJ || '-',
        _moduleGroup: logGroup.entries?.[0]?.module_type || '-',
        _version: logGroup.entries?.[0]?.version || '-',
        _totalHours: logGroup.totalHours || '-',
        _espCostNet: formatValue(inv.espCostNet, 'PLN'),
        _espCostGross: formatValue(inv.espCostGross, 'PLN'),
        _espCostNetEur: formatValue(inv.espCostNetEUR || calcEur(inv.espCostNet), 'EUR'),
        _espCostGrossEur: formatValue(inv.espCostGrossEUR || calcEur(inv.espCostGross), 'EUR'),
        _eldCostEur: formatValue(inv.espCostPrcEUR, 'EUR'),
        _totalCost: formatValue(inv.espCostTotal, 'EUR'),

        _netPaid1: formatValue(netPaid1, 'PLN'),
        _date1: formatDate(inv.dateInvoice1),
        _netPaid1Eur: formatValue(calcEur(netPaid1), 'EUR'),
        _grossPaid1: formatValue(grossPaid1, 'PLN'),
        _grossPaid1Eur: formatValue(calcEur(grossPaid1), 'EUR'),
        _invNum1: inv.invoiceNumber1 || '-',
        _payment1Conf:
          inv.payment1Confirmed === true || inv.payment1Confirmed === 'true' ? 'Yes' : inv.payment1Confirmed || '-',

        _netPaid2: formatValue(netPaid2, 'PLN'),
        _date2: formatDate(inv.dateInvoice2),
        _netPaid2Eur: formatValue(calcEur(netPaid2), 'EUR'),
        _grossPaid2: formatValue(grossPaid2, 'PLN'),
        _grossPaid2Eur: formatValue(calcEur(grossPaid2), 'EUR'),
        _invNum2: inv.invoiceNumber2 || '-',
        _payment2Conf:
          inv.payment2Confirmed === true || inv.payment2Confirmed === 'true' ? 'Yes' : inv.payment2Confirmed || '-',

        _netFiltered: formatValue(totalNetPaid, 'PLN'),
        _netFilteredEur: formatValue(calcEur(totalNetPaid), 'EUR'),
        _grossPaid: formatValue(totalGrossPaid, 'PLN'),
        _grossPaidEur: formatValue(calcEur(totalGrossPaid), 'EUR'),

        _payNetFiltered: formatValue(inv.netPayable, 'PLN'),
        _payNetFilteredEur: formatValue(calcEur(inv.netPayable), 'EUR'),
        _payGross: formatValue(inv.grossPayable, 'PLN'),
        _payGrossEur: formatValue(calcEur(inv.grossPayable), 'EUR'),

        _completionDate: formatDate(inv.completionDate) || inv.monthYearCompletion || '-',

        _allStages: [...stagesSet].join(', ') || '-',
        _invStatus: inv.invoiceStatus || '-',
        _comments: inv.comment || '-',
        _startStage: inv.startStage || '-',
        _conv: inv.conv || '-',
      };
    });
  }, [data, allInvoices]);

  const handleExport = () => {
    if (activeIndex === 0) {
      onCreateGoogleSheet(reportMetrics);
    } else {
      const now = new Date();
      const dateStr =
        now.getFullYear() + String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0');
      const fileName = `filtered_records_${dateStr}`;

      const columns = [
        { field: '_gj', header: dictionary.esp_filtered_col_gj },
        { field: 'esp', header: dictionary.esp_filtered_col_esp },
        { field: '_moduleGroup', header: dictionary.esp_filtered_col_module_group },
        { field: 'country', header: dictionary.esp_filtered_col_country },
        { field: 'lang', header: dictionary.esp_filtered_col_lang },
        { field: 'module', header: dictionary.esp_filtered_col_module },
        { field: '_version', header: dictionary.esp_filtered_col_version },
        { field: '_startStage', header: dictionary.esp_filtered_col_start_stage },
        { field: '_allStages', header: dictionary.esp_filtered_col_module_stage },
        { field: '_conv', header: dictionary.esp_filtered_col_euro },
        { field: '_totalHours', header: dictionary.esp_filtered_col_total_hours },

        { field: '_espCostNet', header: dictionary.esp_filtered_col_cost_net },
        { field: '_espCostGross', header: dictionary.esp_filtered_col_cost_gross },
        { field: '_espCostNetEur', header: dictionary.esp_filtered_col_cost_net_eur },
        { field: '_espCostGrossEur', header: dictionary.esp_filtered_col_cost_gross_eur },
        { field: '_eldCostEur', header: dictionary.esp_filtered_col_eld_cost_eur },
        { field: '_totalCost', header: dictionary.esp_filtered_col_total_cost_eur },

        { field: '_netPaid1', header: dictionary.esp_filtered_col_net_paid_1 },
        { field: '_date1', header: dictionary.esp_filtered_col_date_1 },
        { field: '_netPaid1Eur', header: dictionary.esp_filtered_col_net_paid_eur_1 },
        { field: '_grossPaid1', header: dictionary.esp_filtered_col_gross_paid_1 },
        { field: '_grossPaid1Eur', header: dictionary.esp_filtered_col_gross_paid_eur_1 },
        { field: '_invNum1', header: dictionary.esp_filtered_col_inv_num_1 },
        { field: '_payment1Conf', header: dictionary.esp_filtered_col_pay_conf_1 },

        { field: '_netPaid2', header: dictionary.esp_filtered_col_net_paid_2 },
        { field: '_date2', header: dictionary.esp_filtered_col_date_2 },
        { field: '_netPaid2Eur', header: dictionary.esp_filtered_col_net_paid_eur_2 },
        { field: '_grossPaid2', header: dictionary.esp_filtered_col_gross_paid_2 },
        { field: '_grossPaid2Eur', header: dictionary.esp_filtered_col_gross_paid_eur_2 },
        { field: '_invNum2', header: dictionary.esp_filtered_col_inv_num_2 },
        { field: '_payment2Conf', header: dictionary.esp_filtered_col_pay_conf_2 },

        { field: '_netFiltered', header: dictionary.esp_filtered_col_net_paid },
        { field: '_netFilteredEur', header: dictionary.esp_filtered_col_net_paid_eur },
        { field: '_grossPaid', header: dictionary.esp_filtered_col_gross_paid },
        { field: '_grossPaidEur', header: dictionary.esp_filtered_col_gross_paid_eur },

        { field: '_payNetFiltered', header: dictionary.esp_filtered_col_net_payable },
        { field: '_payNetFilteredEur', header: dictionary.esp_filtered_col_net_payable_eur },
        { field: '_payGross', header: dictionary.esp_filtered_col_gross_payable },
        { field: '_payGrossEur', header: dictionary.esp_filtered_col_gross_payable_eur },

        { field: '_completionDate', header: dictionary.esp_filtered_col_comp_date },
        { field: '_invStatus', header: dictionary.esp_filtered_col_inv_status },
        { field: '_comments', header: dictionary.esp_filtered_col_comments },
      ];

      let mdText = `# ${fileName}\n\nData:\n\n`;
      mdText += '| ' + columns.map((c) => c.header).join(' | ') + ' |\n';
      mdText += '|' + columns.map(() => ' :--- ').join('|') + '|\n';

      excelTableData.forEach((row) => {
        mdText +=
          '| ' +
          columns
            .map((c) => {
              let val = row[c.field];
              if (val === null || val === undefined) val = '';
              return String(val).replace(/\|/g, '').replace(/\n/g, ' ');
            })
            .join(' | ') +
          ' |\n';
      });

      navigator.clipboard
        .writeText(mdText)
        .then(() => {
          const gemUrl = 'https://gemini.google.com/gem/16HBo00jKmoq8NCb4AKE1p3DViq8LKVyQ?usp=sharing';
          window.open(gemUrl, '_blank');
          alert(
            `Data copied to clipboard!\n\nTarget filename: ${fileName}\n\nPaste the data (Ctrl+V) into Gemini to generate the report.`
          );
        })
        .catch((err) => console.error('Copy error:', err));
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={dictionary.esp_filtered_export_btn || 'Export to Sheets'}
        icon="pi pi-external-link"
        onClick={handleExport}
        className="p-button-success"
      />
    </div>
  );

  return (
    <Dialog
      header={dictionary.esp_filtered_dialog_title || 'Filtered Results Analysis'}
      visible={visible}
      onHide={onHide}
      footer={footer}
      maximizable
      maximized={maximized}
      onMaximize={(e) => setMaximized(e.maximized)}
      style={{ width: '90vw' }}
      className="esp-report-dialog"
    >
      <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header={dictionary.esp_filtered_tab_summary || 'Summary Metrics'}>
          <DataTable value={reportMetrics} size="small" stripedRows showGridlines>
            <Column field="id" header="Lp." style={{ width: '3rem' }} />
            <Column field="name" header="Metric Name" />
            <Column field="value" header="Value" body={(r) => <span className="metric-value-text">{r.value}</span>} />
            <Column field="desc" header="Description" />
          </DataTable>
        </TabPanel>

        <TabPanel header={dictionary.esp_filtered_tab_records || 'Filtered Records'}>
          <DataTable
            value={excelTableData}
            size="small"
            stripedRows
            scrollable
            scrollHeight="calc(100vh - 300px)"
            className="filtered-records-table"
            showGridlines
            paginator
            rows={25}
          >
            <Column field="_gj" header={dictionary.esp_filtered_col_gj} frozen alignFrozen="left" />
            <Column field="esp" header={dictionary.esp_filtered_col_esp} frozen alignFrozen="left" />
            <Column field="_moduleGroup" header={dictionary.esp_filtered_col_module_group} />
            <Column field="country" header={dictionary.esp_filtered_col_country} />
            <Column field="lang" header={dictionary.esp_filtered_col_lang} />
            <Column field="module" header={dictionary.esp_filtered_col_module} />
            <Column field="_version" header={dictionary.esp_filtered_col_version} />
            <Column field="_startStage" header={dictionary.esp_filtered_col_start_stage} />
            <Column field="_allStages" header={dictionary.esp_filtered_col_module_stage} />
            <Column field="_conv" header={dictionary.esp_filtered_col_euro} />
            <Column field="_totalHours" header={dictionary.esp_filtered_col_total_hours} />

            <Column field="_espCostNet" header={dictionary.esp_filtered_col_cost_net} />
            <Column field="_espCostGross" header={dictionary.esp_filtered_col_cost_gross} />
            <Column field="_espCostNetEur" header={dictionary.esp_filtered_col_cost_net_eur} />
            <Column field="_espCostGrossEur" header={dictionary.esp_filtered_col_cost_gross_eur} />
            <Column field="_eldCostEur" header={dictionary.esp_filtered_col_eld_cost_eur} />
            <Column field="_totalCost" header={dictionary.esp_filtered_col_total_cost_eur} />

            <Column field="_netPaid1" header={dictionary.esp_filtered_col_net_paid_1} />
            <Column field="_date1" header={dictionary.esp_filtered_col_date_1} />
            <Column field="_netPaid1Eur" header={dictionary.esp_filtered_col_net_paid_eur_1} />
            <Column field="_grossPaid1" header={dictionary.esp_filtered_col_gross_paid_1} />
            <Column field="_grossPaid1Eur" header={dictionary.esp_filtered_col_gross_paid_eur_1} />
            <Column field="_invNum1" header={dictionary.esp_filtered_col_inv_num_1} />
            <Column field="_payment1Conf" header={dictionary.esp_filtered_col_pay_conf_1} />

            <Column field="_netPaid2" header={dictionary.esp_filtered_col_net_paid_2} />
            <Column field="_date2" header={dictionary.esp_filtered_col_date_2} />
            <Column field="_netPaid2Eur" header={dictionary.esp_filtered_col_net_paid_eur_2} />
            <Column field="_grossPaid2" header={dictionary.esp_filtered_col_gross_paid_2} />
            <Column field="_grossPaid2Eur" header={dictionary.esp_filtered_col_gross_paid_eur_2} />
            <Column field="_invNum2" header={dictionary.esp_filtered_col_inv_num_2} />
            <Column field="_payment2Conf" header={dictionary.esp_filtered_col_pay_conf_2} />

            <Column field="_netFiltered" header={dictionary.esp_filtered_col_net_paid} />
            <Column field="_netFilteredEur" header={dictionary.esp_filtered_col_net_paid_eur} />
            <Column field="_grossPaid" header={dictionary.esp_filtered_col_gross_paid} />
            <Column field="_grossPaidEur" header={dictionary.esp_filtered_col_gross_paid_eur} />

            <Column field="_payNetFiltered" header={dictionary.esp_filtered_col_net_payable} />
            <Column field="_payNetFilteredEur" header={dictionary.esp_filtered_col_net_payable_eur} />
            <Column field="_payGross" header={dictionary.esp_filtered_col_gross_payable} />
            <Column field="_payGrossEur" header={dictionary.esp_filtered_col_gross_payable_eur} />

            <Column field="_completionDate" header={dictionary.esp_filtered_col_comp_date} />
            <Column field="_invStatus" header={dictionary.esp_filtered_col_inv_status} />
            <Column field="_comments" header={dictionary.esp_filtered_col_comments} />
          </DataTable>
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default FilteredSummaryDialog;
